/**
 * Presentation helpers for the catalogue.
 *
 * The rule that matters here: a value we have not published renders as an
 * explicit "Not yet documented", never as a blank cell and never as a guess.
 * Blank cells read as "zero" or "none" to a scanning eye; an explicit gap reads
 * as a gap, which is the truth.
 */
import { Dataset, COVERAGE_FIELDS, datasets } from 'app/data/datasets';

/** The single string used everywhere a registry field is undefined. */
export const NOT_DOCUMENTED = 'Not yet documented';

/** True when a registry field has a usable published value. */
export const isDocumented = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * How many of the fields a buyer diligences on are actually published for this
 * dataset. Deliberately reported as "n of m", not normalised into a score:
 * a composite number invites optimising the number instead of the metadata.
 */
export const coverageOf = (
  dataset: Dataset,
): { documented: number; total: number; missing: string[] } => {
  const missing = COVERAGE_FIELDS.filter(
    field => !isDocumented(dataset[field.key]),
  ).map(field => field.label);

  return {
    documented: COVERAGE_FIELDS.length - missing.length,
    total: COVERAGE_FIELDS.length,
    missing,
  };
};

/**
 * Short mono labels for the publication-rate strip. Keyed off the registry's
 * field keys so a renamed COVERAGE_FIELDS label cannot silently desync; a field
 * with no entry here falls back to its full label rather than disappearing.
 */
const SHORT_LABELS: Partial<Record<keyof Dataset, string>> = {
  historyStart: 'history',
  entities: 'entities',
  universe: 'universe',
  updateFrequency: 'cadence',
  publicationLag: 'lag',
  pointInTime: 'point-in-time',
  identifiers: 'ids',
  rows: 'rows',
  schema: 'schema',
};

export interface FieldCoverage {
  key: keyof Dataset;
  label: string;
  short: string;
  why: string;
  documented: number;
  total: number;
}

/**
 * How many datasets publish each coverage field — our own coverage, published
 * honestly rather than summarised into a flattering single number.
 *
 * Counts only COVERAGE_FIELDS. `columnCount` and `sampleTickers` are measured
 * and real, but deliberately excluded: `columnCount` would double-count width
 * against `schema`, and `sampleTickers` is a display affordance, not a field a
 * buyer diligences on. Completeness must not be inflatable by adding UI sugar.
 */
export const fieldCoverage = (rows: Dataset[] = datasets): FieldCoverage[] =>
  COVERAGE_FIELDS.map(field => ({
    key: field.key,
    label: field.label,
    short: SHORT_LABELS[field.key] ?? field.label.toLowerCase(),
    why: field.why,
    documented: rows.filter(dataset => isDocumented(dataset[field.key])).length,
    total: rows.length,
  }));

/** A dataset counts as "documented coverage" when its history start is published. */
export const hasDocumentedCoverage = (dataset: Dataset): boolean =>
  isDocumented(dataset.historyStart);

/** Entity counts are approximate and published as a floor, hence the "+". */
export const formatEntities = (entities?: number): string =>
  entities === undefined
    ? NOT_DOCUMENTED
    : `${entities.toLocaleString('en-US')}+`;

/** Shortened universe label for the table; the full string stays in the title. */
export const shortUniverse = (universe?: string): string => {
  if (!universe) return NOT_DOCUMENTED;
  return universe.replace(
    'US public (listed & delisted)',
    'US public (listed+delisted)',
  );
};

/** "Since 2012" — derived from historyStart, never from anything else. */
export const sinceYear = (historyStart?: string): string | undefined =>
  historyStart ? historyStart.slice(0, 4) : undefined;

export const CATEGORY_LABELS: Record<Dataset['category'], string> = {
  equity: 'Equity',
  economic: 'Economic',
  sectorial: 'Sectorial',
};
